'use client';

import { dataLayerPush, trackMainNavLinkClick, getBaseUrlJoined } from '@libs/analytics';
import { LinkList, LinkListItem, Picture, PrimaryNav, TextLink } from '@petsmart-ui/sparky';
import { handleServiceClick } from '@libs/experience-api';
import { usePageLayout } from '@libs/common-next';
import type { MouseEvent } from 'react';
import { type CategoryTypes, type RecursiveMenuProps } from './types';
import { getCategoryPath, trackCategoryPath, trackingData } from './utils';

export function RecursiveMenu({ parentCategory, parentCategories, path }: RecursiveMenuProps) {
    const { isAuthenticated } = usePageLayout();

    return parentCategories
        ?.filter(
            (category, index, self) =>
                // NOTE: Remove duplicate categories to avoid phantom menu items on back button click
                // TODO: should be remove if we can get the category stack to work properly with unique IDs
                index === self.findIndex(c => c.sysId === category.sysId)
        )
        .map((category: CategoryTypes) => {
            const {
                sysId,
                type = 'text',
                displayText,
                shopAllCategory,
                link,
                imageUrl,
                categories,
                wrapListItems,
            } = category;

            const categoryPath = getCategoryPath(path, displayText);

            function handleOnClick(event: MouseEvent<Element, globalThis.MouseEvent>) {
                handleServiceClick({
                    authType: isAuthenticated,
                    event,
                    link,
                });

                trackMainNavLinkClick(trackCategoryPath(categoryPath));

                dataLayerPush(
                    trackingData({
                        selection: `${parentCategory} | ${displayText}`,
                        link_url: getBaseUrlJoined(link),
                    })
                );
            }

            if (!categories?.length) {
                if (shopAllCategory) {
                    return null;
                }

                switch (type) {
                    case 'image':
                        return (
                            <PrimaryNav.Item key={`${sysId}-${parentCategory}`} className={`category__item--${type}`}>
                                <TextLink
                                    href={link}
                                    onClick={event => handleOnClick(event)}
                                    title={displayText}
                                    id={displayText}
                                >
                                    <Picture
                                        imgSrc={imageUrl ?? ''}
                                        imgAlt={displayText ?? ''}
                                        imageComponentProps={{
                                            loading: 'lazy',
                                        }}
                                    />
                                    <span className="sparky-u-is-vishidden">{displayText}</span>
                                </TextLink>
                            </PrimaryNav.Item>
                        );
                    case 'text':
                    default:
                        return (
                            <PrimaryNav.Item key={`${sysId}-${parentCategory}`} className={`category__item--${type}`}>
                                <TextLink
                                    href={link}
                                    variant="secondary"
                                    onClick={event => handleOnClick(event)}
                                    id={displayText}
                                >
                                    {displayText}
                                </TextLink>
                            </PrimaryNav.Item>
                        );
                }
            }

            return (
                <PrimaryNav.Item key={`${sysId}-${parentCategory}`} className={`category__list--${type}`}>
                    <LinkList className={wrapListItems ? 'is-legacy-grid' : ''}>
                        <LinkListItem onClick={event => handleOnClick(event)} href={link} text={displayText} />

                        <RecursiveMenu
                            parentCategory={`${parentCategory} | ${displayText}`}
                            parentCategories={categories}
                            path={categoryPath}
                            wrapListItems={Boolean(wrapListItems)}
                        />
                    </LinkList>
                </PrimaryNav.Item>
            );
        });
}
